/**
 * 訂單修改摘要（app-api 詳情的 modifications 欄位）。
 *
 * 客戶不會知道自己的單被後台或配送員改過，所以訂單詳情要主動標出來。
 * 這裡只做純邏輯：判斷有沒有修改、分類文字、金額變化的呈現。
 */

/** 與 app-api helpers/order-modifications 的 CustomerModificationKind 對齊。 */
export type OrderModificationKind = "items" | "amount" | "delivery" | "other";

export interface OrderModification {
  /** 修改時間（ISO） */
  at: string;
  kind: OrderModificationKind;
  /** 訂單總額變化；正數變貴、負數變便宜。舊資料算不出來時為 null。 */
  amount_delta: number | null;
}

const KIND_LABEL: Record<OrderModificationKind, string> = {
  items: "訂購品項有異動",
  amount: "金額調整",
  delivery: "配送資訊有異動",
  other: "訂單內容有異動",
};

/** 分類的顯示文字；未知分類退回「訂單內容有異動」，不顯示原始代號。 */
export const modificationLabel = (kind: string): string =>
  KIND_LABEL[kind as OrderModificationKind] ?? KIND_LABEL.other;

/** 有沒有修改紀錄。空陣列、undefined（舊版 API／練習站）都視為沒有。 */
export const hasModifications = (list?: OrderModification[] | null): boolean =>
  Array.isArray(list) && list.length > 0;

/**
 * 金額變化的顯示文字。
 * 正數是變貴，用「＋$58」；負數是變便宜，用「−$58」。
 * null（舊資料算不出來）或 0 回 null，呼叫端不顯示金額那一行。
 */
export const amountDeltaText = (delta: number | null | undefined): string | null => {
  if (delta == null || delta === 0 || !Number.isFinite(delta)) return null;
  const abs = Math.abs(delta).toLocaleString("en-US");
  return delta > 0 ? `＋$${abs}` : `−$${abs}`;
};

/** 最近一次修改的時間；沒有修改回 null。清單已由後端排序（新到舊）。 */
export const latestModifiedAt = (list?: OrderModification[] | null): string | null =>
  hasModifications(list) ? list![0].at : null;
